import type { Ref } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import SolidMicrophoneIcon from 'LuxKit/Icons/solid/SolidMicrophoneIcon'
import composerVoiceMessages from './composerVoiceMessages'

// A spread rather than a scale, so the ring grows the same distance on every side of a wide button too.
// Fades the colour, not opacity: WebKit desyncs compositor opacity from main-thread box-shadow, flashing a full ring.
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, currentColor 60%, transparent); }
  100% { box-shadow: 0 0 0 ${rem(8)} transparent; }
`

const PulseRing = styled.span`
  position: relative;
  display: inline-flex;
  border-radius: ${props => props.theme.borderRadius.round};

  &.pulsing::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    color: ${props => props.theme.palette.highlight.primary.normalForeground};
    pointer-events: none;
    animation: ${pulse} 1.6s ease-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`

interface Props {
  onClick: () => void
  disabled?: boolean
  isEmphasised?: boolean
  /** Shows a "Speak" label beside the icon */
  labelled?: boolean
  ref?: Ref<HTMLSpanElement>
}

const MICROPHONE_ICON = <SolidMicrophoneIcon />

function ComposerMicrophoneButton({ onClick, disabled = false, isEmphasised = false, labelled = false, ref }: Props) {
  const intl = useIntl()
  const accessibleLabel = intl.formatMessage(composerVoiceMessages.microphoneLabel)

  return (
    <PulseRing ref={ref} className={clsx({ pulsing: isEmphasised })}>
      {labelled && (
        <TextButton
          kind="secondary"
          size="medium"
          shape="round"
          startIcon={MICROPHONE_ICON}
          onClick={onClick}
          disabled={disabled}
          aria-label={accessibleLabel}
        >
          {intl.formatMessage(composerVoiceMessages.speakLabel)}
        </TextButton>
      )}
      {!labelled && (
        <IconButton
          kind="tertiary"
          shape="circle"
          size="small"
          onClick={onClick}
          disabled={disabled}
          aria-label={accessibleLabel}
        >
          {MICROPHONE_ICON}
        </IconButton>
      )}
    </PulseRing>
  )
}

export default ComposerMicrophoneButton
